import React, { useState } from "react";
import ChecklistCard from "../../ChecklistCard.js";
import "../../../styles/ResidentialListing.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import importantNotes from "../../../data/Ontario/importantNotes.js";
import ImportantNotes from "../../ImportantNotes.js";
import BackToTop from "../../BackToTop.js";
import ClauseCard from "../../ClauseCard.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import clauseData from "../../../data/Ontario/clauseData.js";
import clauseCardDesc from "../../../data/Ontario/clauseCardDescription.js";

export default function ResidentialListing() {
  const [ListingData, setListingData] = useState([
    "RECO Info Guide Acknowledgement",
    "Listing Agreement with Schedule A (271)",
    "Related FINTRAC Documents",
    "MLS Data Form(s)",
    "Schedule B1 (upload to listing)",
    "Full REALTOR® MLS Printout",
  ]);

  const [ListingType, setListingType] = useState("MLS");
  const [IsDisclosure, setIsDisclosure] = useState("No");
  const [Is244, setIs244] = useState("No");

  // State for conditional documents that can be added/removed
  const [conditionalDocuments, setConditionalDocuments] = useState({
    "Do you or close relatives own the property?": false,
    "Do you have a delayed offer presentation date or other directions?": false,
    "Does the property have known latent defects?": false,
    "Is this an estate sale?": false,
    "Is there a Power of Attorney for the Seller?": false,
    "Co-Listing with another Brokerage?": false,
  });

  // Mapping of checkbox labels to their corresponding listing data strings
  const documentMapping = {
    "Do you or close relatives own the property?":
      "Registrant's Disclosure of Interest - Disposition (161)",
    "Do you have a delayed offer presentation date or other directions?":
      "Seller's Direction re: Property/Offers (244)",
    "Does the property have known latent defects?":
      "Seller Disclosure: Latent Defects (299)",
    "Is this an estate sale?":
      "Copy of the Will/Certificate of Appointment (see notes)",
    "Is there a Power of Attorney for the Seller?":
      "Copy of Valid POA Document",
    "Co-Listing with another Brokerage?": "Co-Brokerage Agreement (650)",
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: "#506CAA",
        dark: "#506CAA",
      },
      text: {
        primary: "#31303F",
        secondary: "#506CAA",
      },
    },
  });

  const setExclusive = (e) => {
    setListingType("Exclusive");
    let newListingData = ListingData;
    if (
      newListingData.includes(
        "CREA - Duty of Cooperation Consent/Disclosure Form",
      ) === true
    ) {
      return;
    } else {
      newListingData.push("CREA - Duty of Cooperation Consent/Disclosure Form");
    }
    setListingData(
      newListingData.filter(
        (item) =>
          item !== "Full REALTOR® MLS Printout" && item !== "MLS Data Form(s)",
      ),
    );
  };

  const setMLS = (e) => {
    setListingType("MLS");
    let newListingData = ListingData;
    if (
      newListingData.includes(
        "CREA - Duty of Cooperation Consent/Disclosure Form",
      ) === false
    ) {
      return;
    } else {
      newListingData.push("MLS Data Form(s)");
      newListingData.push("Full REALTOR® MLS Printout");
    }
    setListingData(
      newListingData.filter(
        (item) => item !== "CREA - Duty of Cooperation Consent/Disclosure Form",
      ),
    );
  };

  // Function to handle conditional document checkboxes
  const toggleConditionalDocument = (documentName) => {
    setConditionalDocuments((prev) => {
      const newState = {
        ...prev,
        [documentName]: !prev[documentName],
      };

      // Get the corresponding string for the listing data
      const listingDataString = documentMapping[documentName];

      // Update Is244 state if this is the delayed offer presentation checkbox
      if (
        documentName ===
        "Do you have a delayed offer presentation date or other directions?"
      ) {
        setIs244(newState[documentName] ? "Yes" : "No");
      }
      if (documentName === "Do you or close relatives own the property?") {
        setIsDisclosure(newState[documentName] ? "Yes" : "No");
      }

      // Update the listing data based on checkbox state
      setListingData((prevListingData) => {
        let newListingData = [...prevListingData];

        if (newState[documentName]) {
          // Add document if checked and not already present
          if (!newListingData.includes(listingDataString)) {
            newListingData.push(listingDataString);
          }
        } else {
          // Remove document if unchecked
          newListingData = newListingData.filter(
            (item) => item !== listingDataString,
          );
        }

        return newListingData;
      });

      return newState;
    });
  };

  return (
    <div className="checklists w-full flex flex-col items-center justify-center">
      <div className="flex flex-col w-full pl-4 ">
        <div className="flex flex-col items-left pb-4">
          <div className="pr-5 ">MLS or Exclusive Listing?</div>
          <ThemeProvider theme={theme}>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                defaultValue="MLS"
              >
                <FormControlLabel
                  value="MLS"
                  control={<Radio size="small" onClick={setMLS} />}
                  label="MLS"
                />
                <FormControlLabel
                  value="Exclusive"
                  control={<Radio size="small" onClick={setExclusive} />}
                  label="Exclusive"
                />
              </RadioGroup>
            </FormControl>
          </ThemeProvider>
        </div>

        {/* Check all that apply section */}
        <div className="flex flex-col items-left pb-4">
          <div className="pr-5 pb-2 ">
            For each "Yes" response, check the box that applies:
          </div>
          <ThemeProvider theme={theme}>
            <div className="flex flex-col ">
              {Object.keys(conditionalDocuments).map((documentName) => (
                <FormControlLabel
                  key={documentName}
                  control={
                    <Checkbox
                      size="small"
                      checked={conditionalDocuments[documentName]}
                      onChange={() => toggleConditionalDocument(documentName)}
                    />
                  }
                  label={documentName}
                />
              ))}
            </div>
          </ThemeProvider>
        </div>

        <div className="paragraph">
          <p>
            <strong>Seller Net Sheet & GeoWarehouse: </strong>A seller net sheet
            gives homeowners a general estimate of their proceeds after
            expenses, while helping listing agents identify potential issues
            early and determine whether the property is saleable based on the
            seller's expectations and the property's financials. Additionally,
            it is wise to obtain the Parcel Register Report in GeoWarehouse to
            see a list of registered liens, mortgages, and encumbrances on the
            property. <br />
            <a
              href="https://drive.google.com/drive/folders/1Nrze9PIqWi_OQQZ8dEdI8nP4j9inRiJD"
              rel="noopener noreferrer"
              target="_blank"
            >
              Download the Seller Net Sheet
            </a>
            <br />
            <br />
          </p>
        </div>
      </div>
      {IsDisclosure === "Yes" ? (
        <div className="clause-card-content">
          <div className="clause-warning">
            <div className="clause-warning-icon">
              <FontAwesomeIcon icon={faTriangleExclamation} size="lg" />
            </div>
            <div>
              As per TRESA, you cannot represent both clients in a trade when
              you have interest in the listed property. As a result, if you
              bring a Buyer client to the trade, you must release or refer one
              of the client(s) to another agent. Additionally, you can collect a
              referral fee on the client referral.
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
      <ChecklistCard
        province={"ON"}
        Title1={""}
        CheckListData={ListingData}
        Header={ListingType + " Listing:"}
        Header2={"Seller Client"}
      />
      <ClauseCard
        ClauseCardDescription={clauseCardDesc.filter((item) =>
          item.category.includes("resListing"),
        )}
        ClauseDataCommon={clauseData.filter(
          (item) =>
            item.category.includes("forsale") && item.isCommon === "yes",
        )}
        ClauseDataSituational={clauseData.filter(
          (item) => item.category.includes("forsale") && item.isCommon === "no",
        )}
      />
      <div className="important-notes flex flex-col w-full justify-left pt-6">
        <strong>Important Notes:</strong>
        <div className="pb-3"></div>
        {Is244 === "Yes" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("directions") === true,
            )}
          />
        ) : (
          ""
        )}
        {IsDisclosure === "Yes" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("disclosure") === true,
            )}
          />
        ) : (
          ""
        )}
        {ListingType === "Exclusive" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("exclusiveListing") === true,
            )}
          />
        ) : (
          ""
        )}
        <ImportantNotes
          Notes={importantNotes.filter(
            (notes) => notes.category.includes("listing") === true,
          )}
        />
        {ListingType === "MLS" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("mls") === true,
            )}
          />
        ) : (
          ""
        )}
      </div>
      <BackToTop />
    </div>
  );
}
